import React, { useMemo, useState } from "react";
import MarkdownNotes from "../../../../../components/content/MarkdownNotes";
import RichTextDisplay from "../../../../../components/textEditor/RichTextDisplay";
import { classNames } from "../../../../../helpers/classNames";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/solid";
import { getWordCountFromOps } from "../../../../../components/textEditor/Quill-Text-Editor/utils";

/**
 * Read-only rich-text display for a single task's content.
 * Supports three content formats: Quill delta (`task.content`),
 * legacy delta in `task.description.delta`, and legacy block-format
 * in `task.description.blocks`. In truncated mode the content is
 * clamped to one line and expands/collapses on click.
 *
 * @param {object}  props
 * @param {object}  props.task                      - Task object containing content fields
 * @param {boolean} [props.isClientPortalTask]       - Applies client-portal styling and completed strikethrough
 * @param {string}  [props.query]                    - Search query forwarded to RichTextDisplay for highlighting
 * @param {boolean} [props.isTruncated=false]        - Clamps display to one line with expand toggle
 */
const DisplayTaskRichText = ({ task, isClientPortalTask, query, isTruncated = false }) => {
    const [isExpanded, setIsExpanded] = useState(!isTruncated);
    const taskClassName = useMemo(() => isClientPortalTask ? "client-portal-task " : "markdown-notes", [])

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <div className={classNames(
            (task.status === 'completed' && isClientPortalTask) ? 'line-through text-gray-400' : 'text-gray-800',
            "text-left w-full",
            task.status !== 'highlighted' && 'text-gray-400'
        )}>
            {task?.content && (
                <div>
                    {isTruncated ? (
                        <div
                            onClick={toggleExpand}
                            className="cursor-pointer group"
                        >
                            <div className={!isExpanded ? 'line-clamp-1' : ''}>
                                <RichTextDisplay
                                    content={task.content}
                                    thisClassName={taskClassName}
                                    query={query}
                                />
                            </div>
                            <div className="flex justify-end items-center mt-1">
                                <div className="inline-flex items-center px-2 py-1 text-sm text-gray-600 group-hover:text-gray-800">
                                    {isExpanded ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <RichTextDisplay
                            content={task.content}
                            thisClassName={taskClassName}
                            query={query}
                        />
                    )}
                </div>
            )}

            {task?.description?.delta && (
                <div>
                    <RichTextDisplay
                        content={{ ops: task.description.delta }}
                        thisClassName={taskClassName}
                        query={query}
                    />
                </div>
            )}

            {task?.description?.blocks && (
                <>
                    <MarkdownNotes noteRaw={task.description} />
                </>
            )}
        </div>
    );
};

export default DisplayTaskRichText; 